import classNames from 'classnames'
import React from 'react'

import useMyId from '../../../../../../hooks/useMyId'
import Routes, {getHref} from '../../../../../../routes'
import {getBuildStatusIconFromBuild, getBuildStatusTypeFromBuild} from '../../../../../../selectors'
import type {Build} from '../../../../../../services/rest'
import {WaitReasons} from '../../../../../common/BuildQueueInfo/BuildQueueInfo.consts'
import {useTitle} from '../../../../../common/BuildStatus/BuildStatusLink/BuildStatusLink.utils'
import RouterLink from '../../../../../common/RouterLink/RouterLink'
import {usePipelineId} from '../../../hooks/pipeline'
import JobIconStatus from '../JobIconStatus/JobIconStatus'

import styles from './RunStatus.css'

export type BaseProps = {
  className?: string
  withLink?: boolean
  withIcon?: boolean
  pipelineId?: string
  omitUnknown?: boolean
  reused?: boolean
}

type Props = Pick<
  Build,
  | 'id'
  | 'status'
  | 'state'
  | 'parallelized'
  | 'canceledInfo'
  | 'failedToStart'
  | 'statusText'
  | 'waitReason'
  | 'startEstimate'
> &
  BaseProps

const ignoredReasons: string[] = [
  WaitReasons.DEPENDENCY_NOT_READY,
  WaitReasons.WAITING_CHECKING_FOR_CHANGES,
  WaitReasons.WAITING_FOR_THE_FIRST_DEPENDENCY_TO_START,
  WaitReasons.CHECKING_FOR_CHANGES_IN_PROGRESS,
]

export default function RunStatus({
  className,
  withLink = true,
  withIcon = true,
  pipelineId,
  omitUnknown,
  reused,
  ...run
}: Props) {
  const pipelineIdFromUrl = usePipelineId()
  const href = getHref(Routes.PIPELINE_RUN, {
    id: pipelineId ?? pipelineIdFromUrl,
    runId: String(run.id),
  })
  const runStatusType = getBuildStatusTypeFromBuild(run)
  const {statusText, state, waitReason, parallelized, startEstimate} = run
  const statusLabel = reused ? `${statusText} (reused)` : statusText
  const runStatus = useTitle({
    statusText: statusLabel,
    isQueued: state === 'queued',
    waitReason: ignoredReasons.includes(waitReason!) ? '' : waitReason,
    startEstimate,
    omitUnknown,
  })
  const myId = useMyId()
  const icon = getBuildStatusIconFromBuild(run, myId)

  const wrapperClassName = classNames(className, styles[runStatusType])
  const iconElement = withIcon && (
    <JobIconStatus
      parallelized={parallelized}
      icon={icon}
      status={runStatusType}
      className={styles.icon}
    />
  )

  return withLink ? (
    <RouterLink to={href} className={wrapperClassName}>
      {WrapText => (
        <>
          {iconElement}
          <WrapText>{runStatus}</WrapText>
        </>
      )}
    </RouterLink>
  ) : (
    <span className={wrapperClassName}>
      {iconElement}
      {runStatus}
    </span>
  )
}
